import { Stack, Paper } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"

import { Filters } from "@costory/types/filters"
import { ExplorerChartType } from "@costory/types/prisma-client"

import { FilterBar } from "@costory/front/components/FilterBar"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import {
  ChartContext,
  useChartContext,
} from "@costory/front/contexts/ChartContext"
import { ExplorerMainChart } from "@costory/front/pages/Explorer/ExplorerMainChart"
import { useAxesQuery } from "@costory/front/queries/axes"
import { useBusinessMetricsQuery } from "@costory/front/queries/businessMetrics"

const _AdvancedExplorer = ({
  chartType,
  setChartType,
  form,
  handleSaveView,
  isNotOtherNode,
  loading,
  showComparisonChart,
  toggleShowComparisonChart,
  setHideComparisonChart,
}: {
  chartType: ExplorerChartType
  setChartType: (type: ExplorerChartType) => void
  form: UseFormReturnType<Filters>
  handleSaveView: () => void
  isNotOtherNode: boolean
  loading: boolean
  showComparisonChart: boolean
  toggleShowComparisonChart: () => void
  setHideComparisonChart: (val: boolean) => void
}) => {
  const { metricsOptions, groupByOptions, drillDownInto, metrics } =
    useChartContext()
  const selectedMetric = metrics.find((m) => m.id === form.getValues().metricId)

  return (
    <Stack data-intercom-target="costory-tour-digest-modal-advanced-explorer">
      <FilterBar
        form={form}
        metricsOptions={metricsOptions}
        groupByOptions={groupByOptions}
        queryError={false}
        onSaveView={handleSaveView}
        isNotOtherNode={isNotOtherNode}
        loading={loading}
        showComparisonChart={showComparisonChart}
        canToggleWhereClause
        toggleShowComparisonChart={
          chartType != ExplorerChartType.WATERFALL
            ? toggleShowComparisonChart
            : undefined
        }
      />
      <Paper flex={1} px={32} py={24}>
        <Stack h={400} justify="center">
          <ExplorerMainChart
            explorerChartType={chartType}
            filters={form.getValues()}
            drillDownInto={drillDownInto}
            setExplorerChartType={setChartType}
            selectedMetric={selectedMetric}
            showComparisonChart={showComparisonChart}
            setHideComparisonChart={setHideComparisonChart}
            advancedExplorerForm={form}
            filterEventsByChangeDataPointPeriods={true}
            showCrossAddEvent={true}
          />
        </Stack>
      </Paper>
    </Stack>
  )
}

export const AdvancedExplorer = ({
  chartType,
  setChartType,
  form,
  handleSaveView,
  isNotOtherNode,
  loading,
  showComparisonChart,
  toggleShowComparisonChart,
  setHideComparisonChart,
}: {
  chartType: ExplorerChartType
  setChartType: (type: ExplorerChartType) => void
  form: UseFormReturnType<Filters>
  handleSaveView: () => void
  isNotOtherNode: boolean
  loading: boolean
  showComparisonChart: boolean
  toggleShowComparisonChart: () => void
  setHideComparisonChart: (val: boolean) => void
}) => {
  const queryMetrics = useBusinessMetricsQuery()
  const queryAxes = useAxesQuery()
  return (
    <QueryWrapper query={queryAxes}>
      {({ data: axes }) => (
        <QueryWrapper query={queryMetrics}>
          {({ data: metrics }) => (
            <ChartContext.Provider
              value={{
                filters: form.getValues(),
                axes,
                metrics,
                redirectPage: "digest",
              }}
            >
              <_AdvancedExplorer
                chartType={chartType}
                setChartType={setChartType}
                form={form}
                handleSaveView={handleSaveView}
                isNotOtherNode={isNotOtherNode}
                loading={loading}
                showComparisonChart={showComparisonChart}
                toggleShowComparisonChart={toggleShowComparisonChart}
                setHideComparisonChart={setHideComparisonChart}
              />
            </ChartContext.Provider>
          )}
        </QueryWrapper>
      )}
    </QueryWrapper>
  )
}
